/**
 * QuickAddModal
 *
 * Simplified GIF upload modal: Upload file/URL → Convert → Store. The create
 * trigger runs the unified retrieval analysis in the background; approval and
 * curation happen on the Retrieval Review page.
 */

import { useState } from 'react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Progress,
} from '@heroui/react'

import { useToast } from '../../../../hooks'
import { QueueService } from '../../services'

import { FileDropzone } from './FileDropzone'

interface QuickAddModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onSuccess?: () => void
}

type UploadSource = 'file' | 'url' | null

export const QuickAddModal = ({
  isOpen,
  onOpenChange,
  onSuccess,
}: QuickAddModalProps) => {
  const [uploadSource, setUploadSource] = useState<UploadSource>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [url, setUrl] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStage, setUploadStage] = useState<string>('')
  const [uploadedGifId, setUploadedGifId] = useState<string | null>(null)
  const [queuedCount, setQueuedCount] = useState(0)

  const toast = useToast()

  const handleReset = () => {
    setUploadSource(null)
    setSelectedFile(null)
    setUrl('')
    setUploadProgress(0)
    setUploadStage('')
    setUploadedGifId(null)
  }

  const handleClose = () => {
    handleReset()
    onOpenChange(false)
  }

  const handleFileSelect = (file: File | null) => {
    setSelectedFile(file)
    setUploadSource('file')
    setUrl('')
  }

  const handleUrlChange = (value: string) => {
    setUrl(value)
    if (value.trim()) {
      setUploadSource('url')
      setSelectedFile(null)
    }
  }

  const handleUpload = async () => {
    if (!uploadSource || (!selectedFile && !url.trim())) {
      toast.error('Please select a file or enter a URL')
      return
    }

    setIsUploading(true)
    setUploadProgress(10)
    setUploadStage('Preparing...')

    try {
      let result

      if (uploadSource === 'file' && selectedFile) {
        setUploadStage('Reading file...')
        setUploadProgress(20)

        // Convert file to base64
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            const result = reader.result as string
            // Remove data URL prefix (e.g., "data:image/gif;base64,")
            const base64 = result.split(',')[1]
            resolve(base64)
          }
          reader.onerror = reject
          reader.readAsDataURL(selectedFile)
        })

        setUploadStage('Converting and optimizing...')
        setUploadProgress(40)

        result = await QueueService.queueUpload(
          {
            type: 'file',
            data: base64Data,
            mediaType: selectedFile.type,
          },
          {
            maxWidth: 720,
            maxHeight: 720,
            frameRate: 24,
            quality: 95,
          }
        )
      } else if (uploadSource === 'url') {
        setUploadStage('Downloading from URL...')
        setUploadProgress(30)

        result = await QueueService.queueUpload(
          {
            type: 'url',
            data: url.trim(),
          },
          {
            maxWidth: 720,
            maxHeight: 720,
            frameRate: 24,
            quality: 95,
          }
        )
      }

      setUploadProgress(90)
      setUploadStage('Creating MP4 version...')

      // Simulate a brief delay for MP4 creation feedback
      await new Promise((resolve) => setTimeout(resolve, 500))

      setUploadProgress(100)
      setUploadStage('Complete!')

      if (result) {
        setUploadedGifId(result.gifId)
        setQueuedCount((prev) => prev + 1)
        toast.success('GIF added to processing queue!')
        onSuccess?.()
      }
    } catch (error) {
      console.error('Error uploading GIF:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to upload GIF'
      )
      setUploadProgress(0)
      setUploadStage('')
    } finally {
      setIsUploading(false)
    }
  }

  const handleAddAnother = () => {
    setUploadSource(null)
    setSelectedFile(null)
    setUrl('')
    setUploadProgress(0)
    setUploadStage('')
    setUploadedGifId(null)
  }

  const canUpload =
    !isUploading &&
    !uploadedGifId &&
    ((uploadSource === 'file' && selectedFile) ||
      (uploadSource === 'url' && url.trim()))

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="2xl"
      scrollBehavior="inside"
      onClose={handleClose}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold">Quick Add GIF</h2>
              <p className="text-sm text-default-500 font-normal">
                Upload to queue • AI analysis happens later
              </p>
            </ModalHeader>

            <ModalBody className="gap-6">
              {!uploadedGifId ? (
                <>
                  {/* File Upload */}
                  <div>
                    <h3 className="text-sm font-medium mb-2">Upload File</h3>
                    <FileDropzone
                      onFilesChange={(files) => {
                        if (files.length > 0) {
                          handleFileSelect(files[0])
                        } else {
                          handleFileSelect(null)
                        }
                      }}
                      disabled={isUploading}
                    />
                    {selectedFile && (
                      <p className="text-sm text-success-600 mt-2">
                        ✓ Selected: {selectedFile.name}
                      </p>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-default-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-content1 text-default-500">
                        OR
                      </span>
                    </div>
                  </div>

                  {/* URL Input */}
                  <div>
                    <h3 className="text-sm font-medium mb-2">Paste URL</h3>
                    <Input
                      placeholder="https://giphy.com/... or direct GIF URL"
                      value={url}
                      onValueChange={handleUrlChange}
                      disabled={isUploading}
                      size="lg"
                    />
                    <p className="text-xs text-default-500 mt-2">
                      Supports: Giphy, Tenor, Reddit, Imgur, direct links
                    </p>
                  </div>

                  {/* Upload Progress */}
                  {isUploading && (
                    <div className="space-y-2">
                      <Progress
                        value={uploadProgress}
                        color="primary"
                        size="sm"
                        className="w-full"
                      />
                      <p className="text-sm text-default-600 text-center">
                        {uploadStage}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Success State */}
                  <div className="text-center py-6 space-y-4">
                    <div className="text-6xl">✓</div>
                    <div>
                      <h3 className="text-lg font-semibold text-success-600">
                        Uploaded!
                      </h3>
                      <p className="text-sm text-default-600 mt-2">
                        GIF ID: <code className="text-xs">{uploadedGifId}</code>
                      </p>
                    </div>

                    {queuedCount > 0 && (
                      <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
                        <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
                          {queuedCount} {queuedCount === 1 ? 'item' : 'items'}{' '}
                          uploaded this session
                        </p>
                      </div>
                    )}

                    <p className="text-sm text-default-500">
                      Retrieval analysis runs in the background. Review and
                      approve on the Retrieval Review page.
                    </p>
                  </div>
                </>
              )}
            </ModalBody>

            <ModalFooter>
              {!uploadedGifId ? (
                <>
                  <Button
                    variant="light"
                    onPress={onClose}
                    disabled={isUploading}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onPress={handleUpload}
                    isLoading={isUploading}
                    isDisabled={!canUpload}
                  >
                    Add to Queue
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="light" onPress={onClose}>
                    Close
                  </Button>
                  <Button color="primary" onPress={handleAddAnother}>
                    Add Another
                  </Button>
                </>
              )}
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
